import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Download, File, Calendar, Terminal, ShieldCheck, Copy, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { PageHeader } from '@/components/PageHeader';
import { Button } from '@/components/ui/button';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';

interface DownloadFile {
    name: string;
    key: string;
    size: number;
    uploaded: string;
    downloadUrl: string;
}

export default function Downloads() {
    const { t } = useTranslation();
    const [files, setFiles] = useState<DownloadFile[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        fetchDownloads();
    }, []);

    const fetchDownloads = async () => {
        try {
            const token = localStorage.getItem('token');
            const apiUrl = import.meta.env.VITE_API_URL || '';

            const response = await fetch(`${apiUrl}/api/downloads`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error('Failed to fetch downloads');
            }

            const data = await response.json();
            if (data.success) {
                setFiles(data.data);
            } else {
                throw new Error(data.message || 'Failed to fetch downloads');
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
        } finally {
            setLoading(false);
        }
    };

    const formatFileSize = (bytes: number) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const getFileIcon = (filename: string) => {
        if (filename.endsWith('.deb')) return <Badge variant="secondary" className="bg-orange-500/10 text-orange-500 hover:bg-orange-500/20">DEB</Badge>;
        if (filename.endsWith('.rpm')) return <Badge variant="secondary" className="bg-red-500/10 text-red-500 hover:bg-red-500/20">RPM</Badge>;
        return <File className="h-4 w-4 text-muted-foreground" />;
    };

    // Stable "_latest_" aliases always point at the newest published build.
    // latest.json (same folder) lists version, sha256 and size per artifact.
    const buildsBase = 'https://api.airgapsuite.com/api/public/downloads/builds';
    const agentDeb = (arch: 'amd64' | 'arm64') => `${buildsBase}/airgapsuite-device-agent_latest_${arch}.deb`;
    const sdkZip = (arch: 'x86_64' | 'arm64') => `${buildsBase}/airgapsuite-sdk_latest_${arch}.zip`;
    const manifestUrl = `${buildsBase}/latest.json`;

    const agentInstallCommand = `ARCH=$( [ "$(uname -m)" = "x86_64" ] && echo amd64 || echo arm64 )
curl -fSL -o agent.deb "${buildsBase}/airgapsuite-device-agent_latest_\${ARCH}.deb"
sudo dpkg -i agent.deb          # installs, enables and starts airgap-device-agent.service
systemctl status airgap-device-agent.service
ls -l /etc/pixuate/license.jwt  # appears once the device serial is approved`;

    const sdkInstallCommand = `ARCH=$( [ "$(uname -m)" = "x86_64" ] && echo x86_64 || echo arm64 )
curl -fSL -o sdk.zip "${buildsBase}/airgapsuite-sdk_latest_\${ARCH}.zip"
sudo mkdir -p /opt/airgapsuite && sudo unzip -o sdk.zip -d /opt/airgapsuite
# lib/liblicense_sdk.so  include/license_sdk.h  examples/example.cpp`;

    const cliInstallCommand = 'curl -fsSL https://api.airgapsuite.com/install.sh | sh';
    const cliInstallWindows = 'irm https://api.airgapsuite.com/install.ps1 | iex';

    const copyText = async (text: string) => {
        try {
            await navigator.clipboard.writeText(text);
        } catch (error) {
            console.error('Failed to copy to clipboard:', error);
        }
    };

    // One-line installer for the AirGapSuite integration skill (Claude Code + Codex).
    const skillInstallCommand =
        'curl -fsSL https://api.airgapsuite.com/api/public/downloads/builds/airgapsuite-skill-install.sh | sh';
    const skillZipUrl =
        'https://api.airgapsuite.com/api/public/downloads/builds/airgapsuite-integrate-skill_latest.zip';
    const copySkillInstall = async () => {
        try {
            await navigator.clipboard.writeText(skillInstallCommand);
        } catch (error) {
            console.error('Failed to copy skill install command:', error);
        }
    };

    if (loading) {
        return (
            <div className="p-8 flex justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    return (
        <div className="w-full space-y-6 px-4 py-6 sm:px-6 lg:px-8">
            <PageHeader
                eyebrow="Workspace"
                icon={Download}
                title={t('downloads.title')}
                subtitle={t('downloads.subtitle')}
            />

            <Card>
                <CardHeader>
                    <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                        <div>
                            <CardTitle className="flex items-center gap-2">
                                <ShieldCheck className="h-5 w-5 text-primary" />
                                Device Agent
                            </CardTitle>
                            <CardDescription>
                                Debian package that claims the license pre-approved for this device&apos;s hardware
                                serial, installs the Ed25519-signed license to <code>/etc/pixuate/license.jwt</code>,
                                and re-polls every 5 minutes so renewals and revocations propagate.
                            </CardDescription>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <Button asChild>
                                <a href={agentDeb('arm64')} target="_blank" rel="noopener noreferrer">
                                    <Download className="h-4 w-4 mr-2" />
                                    DEB arm64 (Jetson)
                                </a>
                            </Button>
                            <Button asChild variant="outline">
                                <a href={agentDeb('amd64')} target="_blank" rel="noopener noreferrer">
                                    <Download className="h-4 w-4 mr-2" />
                                    DEB amd64
                                </a>
                            </Button>
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="grid gap-3 text-sm text-muted-foreground md:grid-cols-3">
                        <div className="rounded-md border p-3">
                            <div className="font-medium text-foreground">Service</div>
                            <div>airgap-device-agent.service</div>
                        </div>
                        <div className="rounded-md border p-3">
                            <div className="font-medium text-foreground">License file</div>
                            <div>/etc/pixuate/license.jwt</div>
                        </div>
                        <div className="rounded-md border p-3">
                            <div className="font-medium text-foreground">Binding</div>
                            <div>Hardware serial (non-transferable)</div>
                        </div>
                    </div>
                    <div className="rounded-md border bg-muted/40">
                        <div className="flex items-center justify-between border-b px-4 py-2">
                            <div className="flex items-center gap-2 text-sm font-medium">
                                <Terminal className="h-4 w-4" />
                                Install on Debian or Ubuntu (auto-selects architecture)
                            </div>
                            <Button variant="ghost" size="sm" onClick={() => copyText(agentInstallCommand)}>
                                <Copy className="h-4 w-4 mr-2" />
                                Copy
                            </Button>
                        </div>
                        <pre className="overflow-x-auto p-4 text-xs leading-6"><code>{agentInstallCommand}</code></pre>
                    </div>
                    <p className="text-xs text-muted-foreground">
                        Jetson (Orin/Xavier/Nano) is <strong>arm64</strong>; Intel/AMD servers and PCs are{' '}
                        <strong>amd64</strong>. On x86 the licensed process must run as root to read the DMI
                        serial. Checksums and exact versions are in{' '}
                        <a className="underline" href={manifestUrl} target="_blank" rel="noopener noreferrer">latest.json</a>.
                    </p>
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                        <div>
                            <CardTitle className="flex items-center gap-2">
                                <File className="h-5 w-5 text-primary" />
                                License SDK
                            </CardTitle>
                            <CardDescription>
                                C-ABI shared library (<code>liblicense_sdk.so</code> + <code>license_sdk.h</code>) callable
                                from C/C++, Python, Node, Go, Rust and Java. Gates the app on license validity, features and
                                camera count, and decrypts shipped model files. See{' '}
                                <Link className="underline" to="/sdk-docs">SDK documentation</Link>.
                            </CardDescription>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <Button asChild>
                                <a href={sdkZip('arm64')} target="_blank" rel="noopener noreferrer">
                                    <Download className="h-4 w-4 mr-2" />
                                    SDK arm64
                                </a>
                            </Button>
                            <Button asChild variant="outline">
                                <a href={sdkZip('x86_64')} target="_blank" rel="noopener noreferrer">
                                    <Download className="h-4 w-4 mr-2" />
                                    SDK x86_64
                                </a>
                            </Button>
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="rounded-md border bg-muted/40">
                        <div className="flex items-center justify-between border-b px-4 py-2">
                            <div className="flex items-center gap-2 text-sm font-medium">
                                <Terminal className="h-4 w-4" />
                                Install the SDK
                            </div>
                            <Button variant="ghost" size="sm" onClick={() => copyText(sdkInstallCommand)}>
                                <Copy className="h-4 w-4 mr-2" />
                                Copy
                            </Button>
                        </div>
                        <pre className="overflow-x-auto p-4 text-xs leading-6"><code>{sdkInstallCommand}</code></pre>
                    </div>
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Terminal className="h-5 w-5 text-primary" />
                        Command-line client (<code>airgap-license</code>)
                    </CardTitle>
                    <CardDescription>
                        Activate and manage licenses, check out floating seats, verify license files offline, and
                        encrypt model files with a product data key at build time. Static Go binaries for Linux,
                        macOS and Windows on amd64 and arm64, installed to <code>~/.airgapsuite/bin</code>.
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="rounded-md border bg-muted/40">
                        <div className="flex items-center justify-between border-b px-4 py-2">
                            <div className="flex items-center gap-2 text-sm font-medium">
                                <Terminal className="h-4 w-4" />
                                macOS / Linux
                            </div>
                            <Button variant="ghost" size="sm" onClick={() => copyText(cliInstallCommand)}>
                                <Copy className="h-4 w-4 mr-2" />
                                Copy
                            </Button>
                        </div>
                        <pre className="overflow-x-auto p-4 text-xs leading-6"><code>{cliInstallCommand}</code></pre>
                    </div>
                    <div className="rounded-md border bg-muted/40">
                        <div className="flex items-center justify-between border-b px-4 py-2">
                            <div className="flex items-center gap-2 text-sm font-medium">
                                <Terminal className="h-4 w-4" />
                                Windows (PowerShell)
                            </div>
                            <Button variant="ghost" size="sm" onClick={() => copyText(cliInstallWindows)}>
                                <Copy className="h-4 w-4 mr-2" />
                                Copy
                            </Button>
                        </div>
                        <pre className="overflow-x-auto p-4 text-xs leading-6"><code>{cliInstallWindows}</code></pre>
                    </div>
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                        <div>
                            <CardTitle className="flex items-center gap-2">
                                <Terminal className="h-5 w-5 text-primary" />
                                AI Agent Skill (Claude Code &amp; Codex)
                            </CardTitle>
                            <CardDescription>
                                Installs the <code>airgapsuite-integrate</code> skill so an AI coding agent can
                                download the device agent + SDK and wire licensing into your app. One command sets it
                                up for both Claude Code and OpenAI Codex.
                            </CardDescription>
                        </div>
                        <Button asChild variant="outline">
                            <a href={skillZipUrl} target="_blank" rel="noopener noreferrer">
                                <Download className="h-4 w-4 mr-2" />
                                Download skill
                            </a>
                        </Button>
                    </div>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="rounded-md border bg-muted/40">
                        <div className="flex items-center justify-between border-b px-4 py-2">
                            <div className="flex items-center gap-2 text-sm font-medium">
                                <Terminal className="h-4 w-4" />
                                Install the skill (Claude Code + Codex)
                            </div>
                            <Button variant="ghost" size="sm" onClick={copySkillInstall}>
                                <Copy className="h-4 w-4 mr-2" />
                                Copy
                            </Button>
                        </div>
                        <pre className="overflow-x-auto p-4 text-xs leading-6"><code>{skillInstallCommand}</code></pre>
                    </div>
                    <p className="text-xs text-muted-foreground">
                        Idempotent — re-run to upgrade. Installs to <code>~/.claude/skills/</code> and{' '}
                        <code>~/.codex/skills/</code>; restart the tool to load it.
                    </p>
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle>{t('downloads.availablePackages')}</CardTitle>
                    <CardDescription>
                        {t('downloads.description')}
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    {error ? (
                        <div className="request-notice error !m-0" role="alert">
                            <AlertCircle className="od-icon" />
                            <span>{error}</span>
                        </div>
                    ) : files.length === 0 ? (
                        <div className="empty-state">
                            <p>{t('downloads.noDownloads')}</p>
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>{t('downloads.table.type')}</TableHead>
                                    <TableHead>{t('downloads.table.filename')}</TableHead>
                                    <TableHead>{t('downloads.table.size')}</TableHead>
                                    <TableHead>{t('downloads.table.uploaded')}</TableHead>
                                    <TableHead className="text-right">{t('downloads.table.action')}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {files.map((file) => (
                                    <TableRow key={file.key}>
                                        <TableCell>{getFileIcon(file.name)}</TableCell>
                                        <TableCell className="font-medium">{file.name}</TableCell>
                                        <TableCell>{formatFileSize(file.size)}</TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-2 text-muted-foreground">
                                                <Calendar className="h-3 w-3" />
                                                {format(new Date(file.uploaded), 'MMM d, yyyy HH:mm')}
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                asChild
                                            >
                                                <a href={`${import.meta.env.VITE_API_URL || ''}${file.downloadUrl}`} target="_blank" rel="noopener noreferrer">
                                                    <Download className="h-4 w-4 mr-2" />
                                                    {t('downloads.download')}
                                                </a>
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
